import { NEXT_TEXTURES } from './nextTextures.js';
// Celestial body data for the solar system
// Distances and sizes are scaled for visual appeal, not scientific accuracy

export const celestialData = {
  sun: {
    name: 'Sun',
    type: 'Star',
    description: 'The Sun is the star at the center of our Solar System. It is a nearly perfect sphere of hot plasma, heated to incandescence by nuclear fusion reactions in its core.',
    radius: 15,
    distance: 0,
    // Rotation rates below are rad/sec at timeSpeed=1, calibrated so one
    // Earth solar day = 24 units of sim time. Every other body is scaled
    // by (24 / its_rotation_hours). Venus, Uranus, Pluto rotate positive
    // here — their >90° axial tilt handles the visual retrograde direction.
    // Known simplification: all tilts are applied as rotation.z, so every
    // pole leans toward the same scene azimuth; real pole right ascensions differ.
    rotationSpeed: 0.0236, // Sun rotates once every ~25.4 Earth days (equatorial)
    orbitalSpeed: 0,
    tilt: 7.25,
    realDiameter: '1,391,400 km',
    realDistance: '0 km',
    orbitalPeriod: 'N/A',
    moons: 0,
    texture: 'sun',
    colors: ['#ffd93d', '#ff9500'],
    glowColor: '#ffaa00'
  },
  mercury: {
    name: 'Mercury',
    type: 'Terrestrial Planet',
    description: 'Mercury is the smallest planet in our Solar System and the closest to the Sun. Its surface is heavily cratered and similar in appearance to the Moon.',
    radius: 1.5,
    distance: 70,
    rotationSpeed: 0.01023, // 1407.5h sidereal rotation (3:2 spin-orbit resonance)
    orbitalSpeed: 0.008,
    // Orbital shape — semi-major axis = distance above. Eccentricity and
    // inclination from NASA Planetary Fact Sheet. Inclination in radians.
    eccentricity: 0.2056,
    orbitalInclination: 0.1222,  // 7.00°
    tilt: 0.034,
    realDiameter: '4,879 km',
    realDistance: '57.9M km',
    orbitalPeriod: '88 days',
    moons: 0,
    texture: 'mercury',
    colors: ['#9a9a9a', '#5c5c5c'],
    glowColor: '#888888'
  },
  venus: {
    name: 'Venus',
    type: 'Terrestrial Planet',
    description: 'Venus is the second planet from the Sun. It has a thick, toxic atmosphere filled with carbon dioxide and is perpetually shrouded in yellowish clouds of sulfuric acid.',
    radius: 2.5,
    distance: 100,
    rotationSpeed: 0.00247, // 5832.5h sidereal rotation; retrograde comes from 177.4° tilt
    orbitalSpeed: 0.006,
    eccentricity: 0.0068,
    orbitalInclination: 0.0592,  // 3.39°
    tilt: 177.4,
    realDiameter: '12,104 km',
    realDistance: '108.2M km',
    orbitalPeriod: '225 days',
    moons: 0,
    texture: 'venus_atmosphere',
    colors: ['#e6c88a', '#c9a227'],
    glowColor: '#daa520',
    hasAtmosphere: true,
    // Tightened atmospheric halo (was 1.04). Even though Venus's real
    // atmosphere is the densest in the solar system, from space the
    // visible halo OUTSIDE the cloud-top disc is thin — what we see
    // IS the cloud top. Earlier 4% shell read as a fat haze ring not
    // present in real Magellan/Akatsuki imagery.
    atmosphereScale: 1.01,
    atmosphereColor: 0xf4deaa,   // warm sulphuric cream
    atmosphereStrength: 0.95,    // densest atmosphere in the solar system
    // Venus has almost no visible Rayleigh component — its optically
    // thick sulphuric cloud deck is Mie-dominated and reads as a
    // uniform pale yellow across all angles. Zeroing Rayleigh keeps
    // Earth's sky-blue out of Venus' halo.
    rayleighTint: 0xf0c860,
    rayleighStrength: 0.05,
    limbTint: 0xf4d590,
    // Terminator stays in the sulphuric cream family rather than
    // inheriting Earth's amber default.
    twilightTint: 0xf2dcb0,
    // Backlit forward-scatter ring: near inferior conjunction Venus's
    // dense atmosphere refracts sunlight around the entire night-side
    // limb (the crescent horns close into a full luminous circle, as
    // in Mariner 10 / Akatsuki imagery). Strongest in the system.
    forwardScatterRing: 0.85
  },
  earth: {
    name: 'Earth',
    type: 'Terrestrial Planet',
    description: 'Earth is the third planet from the Sun and the only astronomical object known to harbor life. About 71% of its surface is covered with water.',
    radius: 2.5,
    distance: 140,
    rotationSpeed: 0.6, // Reference: 24h solar day
    orbitalSpeed: 0.005,
    eccentricity: 0.0167,
    orbitalInclination: 0,       // Earth's orbit defines the ecliptic
    tilt: 23.44,
    realDiameter: '12,742 km',
    realDistance: '149.6M km',
    orbitalPeriod: '365.256 days',
    moons: 1,
    texture: 'earth',
    hasAtmosphere: true,
    // Earth atmosphere dialled in via the in-app planet tuner and baked
    // here at the user's request ("seems to work"). These are stronger
    // than the earlier ultra-thin baseline — a more present limb glow
    // and a fuller Rayleigh day-side wash. atmosphereColor pushed to a
    // muted blue-grey; rayleigh + limb keep the cyan rim.
    atmosphereScale: 1.013,
    atmosphereColor: 0x82aeba,
    atmosphereStrength: 1.50,
    rayleighTint: 0x82c6dc,
    rayleighStrength: 0.50,
    limbTint: 0xc6e8f4,
    // Polar aurora tint re-enabled on the atmosphere shader. Earlier
    // it was off because the cone-curtain meshes handled the visible
    // aurora — but those produced "hair" silhouettes at oblique camera
    // angles and have been hidden by default. The atmosphere-shader
    // green is now the only visible aurora in casual orbit; it lives
    // inside the limb halo so no separate geometry can silhouette.
    // The blue → green transition is interpolated via OKLCh (Bujack
    // 2025 geodesic-style perceptual mix) so the mid-tone reads as
    // vivid teal/aqua instead of muddy grey.
    auroraTint: 0x4cff8c,
    // Atmosphere-shell tint dialled WAY down — the dedicated cone-
    // curtain meshes are the visual lead for the aurora now (they
    // are back, shortened, with no hair silhouette). The shell tint
    // is just a faint complementary glow at the polar limb when
    // viewing from further out where the curtains are sub-pixel.
    // Aurora shell tint disabled — the user wants no green lines on the
    // Earth disc (real astronaut/satellite shots don't show a permanent
    // green oval). The aurora is the night-side emission shell instead
    // (createEarthAuroras in main.js, earthAuroraFragmentShader).
    auroraStrength: 0.0,
    hasClouds: true,
    colors: ['#4a9eff', '#1e5631'],
    glowColor: '#4a9eff',
    moon: {
      name: 'Moon',
      type: 'Natural Satellite',
      description: 'The Moon is Earth\'s only natural satellite. It is the fifth largest moon in the Solar System and the largest relative to its planet.',
      radius: 0.7,
      distance: 12,
      orbitalSpeed: 0.02,
      texture: 'moon',
      realDiameter: '3,475 km',
      realDistance: '384,400 km',
      orbitalPeriod: '27.3 days',
      moons: 0,
      colors: ['#c0c0c0', '#808080'],
      glowColor: '#a0a0a0'
    }
  },
  mars: {
    name: 'Mars',
    type: 'Terrestrial Planet',
    description: 'Mars is the fourth planet from the Sun and is often called the "Red Planet" due to its reddish appearance caused by iron oxide on its surface.',
    radius: 2.0,
    distance: 200,
    rotationSpeed: 0.584, // 24.66h solar day
    orbitalSpeed: 0.004,
    eccentricity: 0.0934,
    orbitalInclination: 0.0323,  // 1.85°
    tilt: 25.19,
    realDiameter: '6,779 km',
    realDistance: '228.0M km',
    orbitalPeriod: '687 days',
    moons: 2,
    texture: 'mars',
    // Real Mars from space is butterscotch/tan (Viking, Curiosity, MRO),
    // not the saturated brick-red of the "Red Planet" myth. The previous
    // #c1440e was over-tinting the texture into deep red.
    colors: ['#c19a6b', '#a67c5a'],
    glowColor: '#d2a679',
    hasAtmosphere: true,
    // Mars's atmosphere is thin but clearly visible at the limb in
    // MRO / Viking imagery as a thin pinkish-tan band. Earlier 1/4
    // thinning (1.0055 + strength 0.14) was below the perceptual
    // threshold; this lands in between so the halo reads from
    // typical zoom without the fat-shell look the original 1.022
    // had. Scale = ~1.2% radius matches the real visible-haze depth.
    atmosphereScale: 1.012,
    atmosphereColor: 0xd9a888,   // dusty tan, less pink than before
    // Per Heckel 2026 / Physically Based Mars Rendering: Mars's
    // atmosphere is Mie-dominated by suspended iron-oxide dust, with
    // a thin red-leaning Rayleigh component. No measurable ozone.
    // Bumped strength so the dust haze reads more clearly at
    // typical orbital viewing distances (was 0.26, now 0.35 matches
    // MRO / HiRise atmospheric brightness vs surface).
    atmosphereStrength: 0.35,
    // rayleighTint pushed toward a deeper iron-oxide rust so the
    // limb glow has the proper Mars butterscotch character. The
    // article's rayleighBeta (0.019, 0.013, 0.0057) has R dominant
    // over B by 3.3x; our new tint preserves that ratio.
    rayleighTint: 0xbe7048,
    rayleighStrength: 0.16,
    limbTint: 0xd49070,
    // The famous BLUE Martian twilight: suspended micron-scale dust
    // forward-scatters blue around the sun while reddening the rest
    // of the sky — Curiosity/Spirit sunset imagery shows a cool
    // blue-grey glow hugging the terminator, the inverse of Earth's
    // amber. This is the single most recognizable Mars-atmosphere
    // signature.
    twilightTint: 0x7fa8e8,
    majorMoons: [
      { name: 'Phobos', type: 'Natural Satellite', description: 'Phobos is the larger of Mars\'s two moons and is slowly spiraling inward toward Mars.', radius: 0.15, distance: 6, orbitalSpeed: 0.08, texture: 'phobos', shape: 'phobos', tidallyLocked: true, realDiameter: '22 km', realDistance: '9,376 km', orbitalPeriod: '0.32 days', moons: 0, colors: ['#968a80', '#4a3f35'], glowColor: '#5a4a3f' },
      { name: 'Deimos', type: 'Natural Satellite', description: 'Deimos is the smaller of Mars\'s two moons, an irregularly shaped asteroid-like body.', radius: 0.1, distance: 10, orbitalSpeed: 0.03, texture: 'deimos', shape: 'deimos', tidallyLocked: true, realDiameter: '12 km', realDistance: '23,458 km', orbitalPeriod: '1.26 days', moons: 0, colors: ['#968a80', '#5a4a3f'], glowColor: '#6a5a4f' }
    ]
  },
  jupiter: {
    name: 'Jupiter',
    type: 'Gas Giant',
    description: 'Jupiter is the largest planet in our Solar System. It is a gas giant with a mass almost two and a half times that of all the other planets combined.',
    radius: 8.0,
    distance: 320,
    rotationSpeed: 1.45, // 9.93h solar day — fastest rotator
    orbitalSpeed: 0.002,
    eccentricity: 0.0489,
    orbitalInclination: 0.0227,  // 1.30°
    tilt: 3.13,
    realDiameter: '139,820 km',
    realDistance: '778.5M km',
    orbitalPeriod: '11.86 years',
    moons: 115,
    texture: 'jupiter',
    colors: ['#d4a574', '#c9a227'],
    glowColor: '#deb887',
    // Atmosphere shell disabled at the user's request — real space
    // photos of Jupiter show no separate atmospheric line at the limb,
    // and the added warm-tan shell read as a foreign halo around the
    // disc. Jupiter now renders as the crisp banded surface (band drift
    // + GRS kept). Tuning values left for reference.
    hasAtmosphere: false,
    atmosphereScale: 1.010,
    atmosphereColor: 0xe0b88a,
    atmosphereStrength: 0.55,
    rayleighTint: 0xc99876,
    rayleighStrength: 0.16,
    limbTint: 0xeac9a0,
    majorMoons: [
      { name: 'Io', type: 'Natural Satellite', description: 'Io is the most volcanically active body in the Solar System. Its surface is covered with sulfur in various colorful forms.', radius: 0.6, distance: 18, orbitalSpeed: 0.04, texture: 'io', realDiameter: '3,643 km', realDistance: '421,800 km', orbitalPeriod: '1.769138 days', moons: 0, colors: ['#f0e68c', '#daa520'], glowColor: '#f0e68c' },
      { name: 'Europa', type: 'Natural Satellite', description: 'Europa has a smooth icy surface hiding a subsurface ocean. It is considered one of the most likely places to find extraterrestrial life.', radius: 0.5, distance: 24, orbitalSpeed: 0.035, texture: 'europa', realDiameter: '3,122 km', realDistance: '671,100 km', orbitalPeriod: '3.551181 days', moons: 0, colors: ['#e0e0e0', '#b0b0b0'], glowColor: '#d0d0d0' },
      { name: 'Ganymede', type: 'Natural Satellite', description: 'Ganymede is the largest moon in the Solar System, even larger than Mercury. It has its own magnetic field.', radius: 0.8, distance: 32, orbitalSpeed: 0.025, texture: 'ganymede', realDiameter: '5,262 km', realDistance: '1,070,400 km', orbitalPeriod: '7.154553 days', moons: 0, colors: ['#a0a0a0', '#707070'], glowColor: '#909090' },
      { name: 'Callisto', type: 'Natural Satellite', description: 'Callisto is the most heavily cratered object in the Solar System. It may also harbor a subsurface ocean.', radius: 0.7, distance: 42, orbitalSpeed: 0.015, texture: 'callisto', realDiameter: '4,821 km', realDistance: '1,882,700 km', orbitalPeriod: '16.689017 days', moons: 0, colors: ['#808080', '#505050'], glowColor: '#707070' }
    ]
  },
  saturn: {
    name: 'Saturn',
    type: 'Gas Giant',
    description: 'Saturn is the sixth planet from the Sun and is famous for its prominent ring system. It is a gas giant with an average radius about nine times that of Earth.',
    radius: 7.0,
    distance: 450,
    rotationSpeed: 1.365, // 10.55h solar day
    orbitalSpeed: 0.0015,
    eccentricity: 0.0565,
    orbitalInclination: 0.0435,  // 2.49°
    tilt: 26.73,
    realDiameter: '116,460 km',
    realDistance: '1.43B km',
    orbitalPeriod: '29.45 years',
    moons: 293,
    texture: 'saturn',
    hasRings: true,
    ringInnerRadius: 8.05,
    ringOuterRadius: 16,
    colors: ['#f4d59e', '#c9a227'],
    glowColor: '#f4a460',
    // Atmospheric haze — Cassini imagery shows Saturn's disc wrapped
    // in a thin dense pale-gold veil from upper-tropospheric ammonia
    // ice + tholin photochemistry. Shell sits close to the surface
    // (0.6% radius) so it reads as a thin atmospheric layer hugging
    // Atmosphere shell disabled at the user's request — the dense gold
    // shell + limb-haze softWash were washing the disc out and reading
    // dull. Saturn now renders as the crisp banded surface (band drift
    // + ring-shadow projection kept). Tuning values left for reference.
    hasAtmosphere: false,
    atmosphereScale: 1.010,
    atmosphereColor: 0xefdcaa,    // pale buttery gold (Cassini photometry)
    atmosphereStrength: 0.58,
    rayleighTint: 0xcaa776,        // soft amber rayleigh
    rayleighStrength: 0.18,
    limbTint: 0xf4e2b4,            // pale gold limb glow
    majorMoons: [
      { name: 'Titan', type: 'Natural Satellite', description: 'Titan is the only moon with a dense atmosphere and the only place besides Earth with stable surface liquid (methane lakes).', radius: 0.8, distance: 32, orbitalSpeed: 0.012, texture: 'titan', hazeVolume: true, realDiameter: '5,150 km', realDistance: '1,221,870 km', orbitalPeriod: '15.95 days', moons: 0, colors: ['#daa520', '#b8860b'], glowColor: '#cd853f', hasAtmosphere: true, atmosphereScale: 1.08, atmosphereColor: 0xe8a058, atmosphereStrength: 0.55, twilightTint: 0xe88030, forwardScatterRing: 0.35 },
      { name: 'Enceladus', type: 'Natural Satellite', description: 'Enceladus has geysers that spray water ice into space. It has a subsurface ocean and is a prime target for finding life.', radius: 0.3, distance: 22, orbitalSpeed: 0.025, texture: 'enceladus', realDiameter: '504 km', realDistance: '238,020 km', orbitalPeriod: '1.37 days', moons: 0, colors: ['#ffffff', '#e0e0e0'], glowColor: '#f0f0f0' },
      { name: 'Rhea', type: 'Natural Satellite', description: 'Rhea is Saturn\'s second-largest moon, an icy body with a heavily cratered surface.', radius: 0.4, distance: 26, orbitalSpeed: 0.018, texture: 'rhea', tidallyLocked: true, realDiameter: '1,527 km', realDistance: '527,040 km', orbitalPeriod: '4.52 days', moons: 0, colors: ['#c0c0c0', '#909090'], glowColor: '#b0b0b0' },
      { name: 'Dione', type: 'Natural Satellite', description: 'Dione has bright ice cliffs and is thought to have a subsurface ocean.', radius: 0.35, distance: 20, orbitalSpeed: 0.022, texture: 'dione', tidallyLocked: true, realDiameter: '1,123 km', realDistance: '377,400 km', orbitalPeriod: '2.74 days', moons: 0, colors: ['#d0d0d0', '#a0a0a0'], glowColor: '#c0c0c0' }
    ]
  },
  uranus: {
    name: 'Uranus',
    type: 'Ice Giant',
    description: 'Uranus is the seventh planet from the Sun. It has the third-largest planetary radius and fourth-largest planetary mass in the Solar System.',
    radius: 4.5,
    distance: 580,
    rotationSpeed: 0.835, // 17.24h; sideways rotation comes from 97.77° tilt
    orbitalSpeed: 0.001,
    eccentricity: 0.0457,
    orbitalInclination: 0.0134,  // 0.77°
    tilt: 97.77,
    realDiameter: '50,724 km',
    realDistance: '2.87B km',
    orbitalPeriod: '84 years',
    moons: 29,
    texture: 'uranus',
    hasRings: true,
    ringInnerRadius: 7.4,
    ringOuterRadius: 9.0,
    colors: ['#a5d6e7', '#4a9eff'],
    glowColor: '#87ceeb',
    // Uranus' atmosphere is dominated by H2 + He with a methane (CH4)
    // upper layer. Methane absorbs red, leaving a pale cyan limb in
    // Voyager 2 / Hubble close-ups. Tucked tight to the disc (0.4%
    // shell) so it reads as atmospheric softening rather than a halo
    // protruding from the planet — Uranus is the most "featureless
    // disc" of the gas/ice giants, so the haze has to be subtle to
    // not dominate the visual. Strength dialled down from the
    // earlier 0.5 to match the more restrained Neptune pass.
    hasAtmosphere: true,
    atmosphereScale: 1.004,
    atmosphereColor: 0xb4dde8,    // pale cyan haze matching the limb
    atmosphereStrength: 0.38,
    rayleighTint: 0x82c6dc,        // methane-absorbed cyan, not earth-blue
    rayleighStrength: 0.12,
    limbTint: 0xc6e8f4,            // bright cyan limb softening
    majorMoons: [
      { name: 'Miranda', type: 'Natural Satellite', description: 'Miranda has one of the most extreme and varied landscapes in the Solar System, with giant canyons.', radius: 0.25, distance: 12, orbitalSpeed: 0.035, texture: 'miranda', tidallyLocked: true, realDiameter: '472 km', realDistance: '129,900 km', orbitalPeriod: '1.41 days', moons: 0, colors: ['#a0a0a0', '#707070'], glowColor: '#909090' },
      { name: 'Ariel', type: 'Natural Satellite', description: 'Ariel is the brightest of Uranus\'s moons with a relatively young surface.', radius: 0.35, distance: 16, orbitalSpeed: 0.028, texture: 'ariel', tidallyLocked: true, realDiameter: '1,158 km', realDistance: '190,900 km', orbitalPeriod: '2.52 days', moons: 0, colors: ['#c0c0c0', '#909090'], glowColor: '#b0b0b0' },
      { name: 'Umbriel', type: 'Natural Satellite', description: 'Umbriel is the darkest of Uranus\'s large moons with ancient, heavily cratered terrain.', radius: 0.35, distance: 20, orbitalSpeed: 0.022, texture: 'umbriel', tidallyLocked: true, realDiameter: '1,169 km', realDistance: '266,000 km', orbitalPeriod: '4.14 days', moons: 0, colors: ['#606060', '#404040'], glowColor: '#505050' },
      { name: 'Titania', type: 'Natural Satellite', description: 'Titania is the largest moon of Uranus, with huge fault systems and canyons.', radius: 0.5, distance: 26, orbitalSpeed: 0.016, texture: 'titania', tidallyLocked: true, realDiameter: '1,578 km', realDistance: '436,300 km', orbitalPeriod: '8.71 days', moons: 0, colors: ['#b0b0b0', '#808080'], glowColor: '#a0a0a0' },
      { name: 'Oberon', type: 'Natural Satellite', description: 'Oberon is the second largest moon of Uranus, heavily cratered with mysterious dark patches.', radius: 0.48, distance: 32, orbitalSpeed: 0.012, texture: 'oberon', tidallyLocked: true, realDiameter: '1,523 km', realDistance: '583,500 km', orbitalPeriod: '13.46 days', moons: 0, colors: ['#909090', '#606060'], glowColor: '#808080' }
    ]
  },
  neptune: {
    name: 'Neptune',
    type: 'Ice Giant',
    description: 'Neptune is the eighth and farthest known planet from the Sun. It is the fourth-largest planet by diameter and the third-most-massive planet.',
    radius: 4.3,
    distance: 700,
    rotationSpeed: 0.894, // 16.11h solar day
    orbitalSpeed: 0.0008,
    eccentricity: 0.0113,
    orbitalInclination: 0.0309,  // 1.77°
    tilt: 28.32,
    realDiameter: '49,244 km',
    realDistance: '4.5B km',
    orbitalPeriod: '164.8 years',
    moons: 16,
    texture: 'neptune',
    // Real Neptune per Irwin et al. 2024 (Oxford / MNRAS) is a pale
    // greenish-blue close to Uranus' shade — NOT the iconic Voyager
    // deep azure, which was contrast-stretched to reveal cloud bands
    // and ended up "too blue". Texture re-processed to land at
    // HSV(197°, 0.23, 0.93) so the rendered disc matches the Oxford
    // truth value of HSV(197°, 0.24, 0.90) after spherical lighting
    // falloff. Palette colours match the new tone.
    colors: ['#b6dcec', '#7095ad'],
    glowColor: '#c8e3ee',
    // Neptune atmospheric veil. Target effect: "real planet" rather
    // than a fat protruding halo. Voyager 2 / JWST close-ups show a
    // very tight azure rim that softens the limb terminator without
    // sticking out from the disc. Shell tucked to 0.6% radius so it
    // hugs the surface; strength moderate so the veil reads as
    // atmospheric softening, not a glow ring.
    hasAtmosphere: true,
    atmosphereScale: 1.006,
    atmosphereColor: 0xa8c9e8,    // soft azure haze
    atmosphereStrength: 0.55,
    rayleighTint: 0x6ea0d2,        // deeper blue Rayleigh than Uranus
    rayleighStrength: 0.18,
    limbTint: 0xd0e4f5,            // bright azure limb softening
    majorMoons: [
      { name: 'Triton', type: 'Natural Satellite', description: 'Triton is the only large moon with a retrograde orbit. It was likely captured from the Kuiper Belt and has active geysers.', radius: 0.5, distance: 20, orbitalSpeed: -0.018, texture: 'triton', realDiameter: '2,705 km', realDistance: '354,800 km', orbitalPeriod: '5.88 days', moons: 0, colors: ['#b0c4de', '#87ceeb'], glowColor: '#add8e6' },
      { name: 'Nereid', type: 'Natural Satellite', description: 'Nereid has one of the most eccentric orbits of any moon, suggesting it was captured.', radius: 0.15, distance: 28, orbitalSpeed: 0.005, texture: 'nereid', realDiameter: '340 km', realDistance: '5,513,400 km', orbitalPeriod: '360.14 days', moons: 0, colors: ['#808080', '#606060'], glowColor: '#707070' }
    ]
  },
  pluto: {
    name: 'Pluto',
    type: 'Dwarf Planet',
    description: 'Pluto is a dwarf planet in the Kuiper belt. It was the first Kuiper belt object to be discovered and remains the largest known one.',
    radius: 1.0,
    distance: 850,
    rotationSpeed: 0.094, // 153.3h solar day; retrograde via the 119.51° tilt
    orbitalSpeed: 0.0005,
    eccentricity: 0.2488,        // highly elliptical; crosses Neptune's orbit
    orbitalInclination: 0.2996,  // 17.16° above ecliptic
    tilt: 119.51, // obliquity to orbit, NASA Pluto fact sheet (2024)
    realDiameter: '2,377 km',
    realDistance: '5.9B km',
    orbitalPeriod: '248 years',
    moons: 5,
    texture: 'pluto',
    colors: ['#c9b896', '#8b7355'],
    glowColor: '#d2b48c',
    majorMoons: [
      { name: 'Charon', type: 'Natural Satellite', description: 'Charon is Pluto\'s largest moon, so large relative to Pluto that they orbit a common center of gravity.', radius: 0.5, distance: 6, orbitalSpeed: 0.025, texture: 'charon', tidallyLocked: true, realDiameter: '1,212 km', realDistance: '19,591 km', orbitalPeriod: '6.39 days', moons: 0, colors: ['#a0a0a0', '#707070'], glowColor: '#909090' }
    ]
  }
};

// Local texture paths
export const textureUrls = {
  // Real mission data or our own maps since 16 Oct 2026 (docs/textures/);
  // ?v= because /textures/ is cached immutable for a year.
  // Generated to the old map's statistics (scripts/textures/build-sun-map.py).
  sun: '/textures/sun_8k.jpg?v=2',
  // MESSENGER: MD3, the 166 m basemap and the colour mosaic.
  mercury: '/textures/mercury.jpg?v=2',
  // Venus keeps its earlier map (Andre preferred it to the baked shader colour).
  venus_atmosphere: '/textures/venus.jpg',
  venus_surface: '/textures/venus.jpg',
  // NASA Blue Marble: Next Generation (June 2004), fitted to the approved look;
  // Black Marble 2016 at night (docs/textures/earth.md).
  earth: '/textures/earth.jpg?v=2',
  earth_night: '/textures/earth_night.jpg?v=2',
  earth_clouds: '/textures/earth_clouds.jpg',
  earth_specular: '/textures/earth_specular.jpg?v=ocean-mask-3',
  // The sea at 8K (scripts/build-earth-ocean.mjs): R water coverage, G and B
  // depth and Kd490 premultiplied by R (src/shaders/earthocean.js).
  earth_ocean: '/textures/earth_ocean.webp?v=2',
  // NOAA ETOPO 2022 relief (CC0), the old map's channel convention kept.
  earth_normal: '/textures/earth_normal.jpg?v=neutral-normal-3',
  // Earth elevation bump map (Planet Pixel Emporium 1K, derived from
  // ETOPO global topography). Used by the Earth shader for cheap
  // horizon-shadow shading on mountain ranges + crater rims, no TBN
  // math involved (which had NaN flicker issues at the silhouette).
  earth_bump: '/textures/earth_bump.jpg',
  moon: '/textures/moon.jpg',
  // Optional bump map for the Moon. Read by createSurfaceMaterial when
  // present; missing the file falls back to flat shading without error.
  // Recommended source: NASA SVS CGI Moon Kit displacement / LROC LOLA
  // shaded relief, downsampled to 4K-8K equirectangular grayscale.
  moon_bump: '/textures/moon_bump.jpg',
  mars: '/textures/mars.jpg',
  // Mars relief from MOLA (MEGDR 64 px/deg, public domain), laid out like
  // mars.jpg (the old one ran 0 to 360 E, so Mars was lit by the far side).
  mars_bump: '/textures/mars_bump.jpg?v=2',
  // Mercury relief from the USGS MESSENGER global DEM (665 m).
  mercury_bump: '/textures/mercury_bump.jpg?v=2',
  // Cassini's PIA07782 detail and polar maps, coloured from NASA's Cassini
  // true-colour portrait PIA04866 (December 2000).
  jupiter: '/textures/jupiter.jpg?v=2',
  // Hubble OPAL 2025 graded like NASA's releases (CC BY 4.0), with Cassini's
  // 2017 north polar hexagon (PIA21611).
  saturn: '/textures/saturn.jpg?v=2',
  // Cassini UVIS occultation optical depth in alpha, Hubble colour in RGB
  // (read with realStrip in the ring shader).
  saturn_ring: '/textures/saturn_ring.png?v=2',
  uranus: '/textures/uranus.jpg',
  neptune: '/textures/neptune.jpg',
  // New Horizons LORRI mosaic coloured from MVIC.
  pluto: '/textures/pluto.jpg?v=2',
  // Dawn colour and clear mosaics.
  ceres: '/textures/ceres.jpg?v=2',
  // New Horizons, coloured from the MVIC global colour map.
  charon: '/textures/charon.jpg',
  stars: '/textures/stars.jpg?v=2',
  // Moon textures, all real mission data: the Galilean moons from USGS
  // Voyager/Galileo mosaics; Titan its visible-light haze (Payne et al. 2025,
  // Cassini banding), Enceladus the USGS Cassini 110 m mosaic, Triton
  // Schenk's Voyager colour mosaic (docs/textures/outer-moons.md).
  // Phobos's own map, from Phil Stooke's Viking mosaic on DLR's control
  // network (USGS Astrogeology), built with its shape by
  // scripts/build-phobos.mjs (docs/textures/phobos.md).
  phobos: '/textures/phobos.jpg',
  deimos: '/textures/deimos.jpg',
  io: '/textures/io.jpg',
  europa: '/textures/europa.jpg',
  europa_bump: '/textures/europa.jpg',
  ganymede: '/textures/ganymede.jpg',
  callisto: '/textures/callisto.jpg',
  titan: '/textures/titan.jpg',
  enceladus: '/textures/enceladus.jpg',
  triton: '/textures/triton.jpg',
  // Relief for Triton's map above (scripts/build-triton-relief.mjs): a
  // tangent-space normal in the map's east/north frame and the height it
  // came from, which the shader marches for shadows in the crevices.
  triton_normal: '/textures/triton_normal.webp?v=2',
  triton_height: '/textures/triton_height.jpg?v=2',
  // Rhea and Dione: the USGS Cassini-Voyager mosaics, their large-scale albedo
  // from Schenk's Cassini colour maps at the measured light curve. The
  // Uranian moons: the USGS Voyager 2 mosaics of the south, the unimaged
  // north and the detail below Voyager's resolution completed to match the
  // data; Nereid from its measured albedo and colour only, not imagery
  // (docs/textures/saturn-uranus-moons.md).
  rhea: '/textures/rhea.jpg',
  dione: '/textures/dione.jpg',
  miranda: '/textures/miranda.jpg',
  ariel: '/textures/ariel.jpg',
  umbriel: '/textures/umbriel.jpg',
  titania: '/textures/titania.jpg',
  oberon: '/textures/oberon.jpg',
  nereid: '/textures/nereid.jpg',
  // The far dwarf planets: nothing has resolved their surfaces, so each map
  // is an artistic rendition drawn from what is measured of the body
  // (scripts/textures/generate-dwarf-map.mjs <body>), at 8K with relief
  // read from it (scripts/textures/build-dwarf-8k.mjs <body>); see
  // docs/textures/dwarf-planets.md. 2026 ED138's is modelled on Pluto and
  // Charon (docs/textures/ed138.md). eris.jpg and haumea.jpg were Solar
  // System Scope maps until October 2026, hence their ?v=2.
  ed138: '/textures/ed138.jpg?v=2',
  ed138_normal: '/textures/ed138_normal.webp',
  ed138_height: '/textures/ed138_height.jpg',
  sedna: '/textures/sedna.jpg',
  sedna_normal: '/textures/sedna_normal.webp',
  sedna_height: '/textures/sedna_height.jpg',
  eris: '/textures/eris.jpg?v=2',
  eris_normal: '/textures/eris_normal.webp',
  eris_height: '/textures/eris_height.jpg',
  makemake: '/textures/makemake.jpg',
  makemake_normal: '/textures/makemake_normal.webp',
  makemake_height: '/textures/makemake_height.jpg',
  haumea: '/textures/haumea.jpg?v=2',
  haumea_normal: '/textures/haumea_normal.webp',
  haumea_height: '/textures/haumea_height.jpg'
};

// ?tex=next on a local server: the staged replacement maps
// (src/data/nextTextures.js, files in public/textures/next/, never deployed)
// in place of the current ones, for comparing old and new before promotion.
if (typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(location.hostname)
  && new URLSearchParams(location.search).get('tex') === 'next') {
  for (const [key, file] of Object.entries(NEXT_TEXTURES)) {
    if (file === null) delete textureUrls[key];
    else textureUrls[key] = `/textures/next/${file}`;
  }
}

// Order of planets for iteration
export const planetOrder = ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'];
